<template>
  <b-card title="Add POI" class="w-100">
    <b-alert
      v-for="(error, index) in errors"
      variant="danger"
      :key="'error_' + index"
      class="mt-1 mb-2 pt-1 pr-3 pb-1 pl-3"
      show
    >
      <div v-if="error.field"><strong>Field:</strong> {{ error.field }}</div>
      <div><strong>Error:</strong> <span v-html="error.error"></span></div>
      <div v-if="error.fix"><strong>Fix:</strong> <span v-html="error.fix"></span></div>
    </b-alert>
    <template v-if="submitSuccess">
      <b-img :src="imagePreviewUrl" fluid alt="preview image" width="auto" class="mb-1" rounded />
      <b-alert variant="success" class="mt-1 mb-0" show>
        <strong>Success:</strong> POI saved.
        <b-button variant="primary" @click="resetFormFields" size="sm">Add new POI</b-button>
        <b-button :to="{ name: 'pois-list' }" class="ml-1" size="sm">Cancel</b-button>
      </b-alert>
      <b-alert class="mt-2 mb-0" show>
        <strong>Info:</strong> Your upload has to be validated before it will be made public.<br />
        <i>
          <small class="text-secondary">
            Note: Unfortunately we are out of
            <span v-b-tooltip.html title="robot<br /><small><i>note: a typical one...</i></small>"
              ><font-awesome-icon :icon="['fas', 'robot']"/></span
            >s. A human has to do this. And humans are slow... Please be patient. Thanks :)
          </small>
        </i>
      </b-alert>
    </template>
    <b-form v-if="!submitSuccess" @submit.prevent="onUpload" @reset="resetFormFields">
      <b-row>
        <b-col>
          <b-form-group
            id="input-group-3"
            label="Image:"
            label-for="input-3"
            description="Note: At the moment only .jpg files are accepted."
            class="mb-0"
          >
            <b-form-file
              id="input-3"
              v-model="form.images.orig"
              accept=".jpg"
              placeholder="Click to choose a file or drop it here..."
              drop-placeholder="Drop file here..."
              @change="onFileChange"
              required
              :disabled="!['waiting', 'complete'].includes(progress.status)"
            ></b-form-file>
          </b-form-group>
          <div class="mt-1">
            <b-img v-if="imagePreviewUrl" :src="imagePreviewUrl" fluid alt="preview image" width="auto" rounded />

            <b-list-group v-if="progress.status !== 'waiting'" class="mt-1">
              <b-list-group-item class="d-flex flex-wrap align-items-center pt-1 pb-1 pl-2 pr-2">
                <template v-if="progress.exif === 'waiting'">
                  <font-awesome-icon :icon="['far', 'clock']" :style="{ color: '#6c757d' }" />
                  <span class="ml-2 text-secondary align-middle">Exif Data: Waiting to start...</span>
                </template>
                <div v-else-if="progress.exif === 'running'">
                  <font-awesome-icon :icon="['fas', 'spinner']" :style="{ color: '#6c757d' }" spin />
                  <span class="ml-2 text-secondary">Exif Data: Running...</span>
                </div>
                <div v-else-if="progress.exif === 'complete'">
                  <font-awesome-icon :icon="['far', 'check-circle']" :style="{ color: '#28a745' }" />
                  <span class="ml-2 text-secondary">Exif Data: Completed</span>
                </div>
                <template v-else>
                  <font-awesome-icon :icon="['far', 'times-circle']" :style="{ color: '#dc354c' }" />
                  <span class="ml-2 text-secondary align-middle">Exif Data: Failure</span>
                  <b-alert variant="danger" class="w-100 mb-0 pt-2 pb-2 pl-3 pr-3" show>
                    Image contains no GPS data. Try to upload a different one.<br />
                    <i>
                      <small class="text-secondary">
                        Note: GPS data is required to display the image where it has been taken.
                      </small>
                    </i>
                  </b-alert>
                </template>
              </b-list-group-item>
              <b-list-group-item class="d-flex align-items-center pt-1 pb-1 pl-2 pr-2">
                <template v-if="progress.image === 'waiting'">
                  <font-awesome-icon :icon="['far', 'clock']" :style="{ color: '#6c757d' }" />
                  <span class="ml-2 text-secondary align-middle">Image Processing: Waiting to start...</span>
                </template>
                <div v-else-if="progress.image === 'running'">
                  <font-awesome-icon :icon="['fas', 'spinner']" :style="{ color: '#6c757d' }" spin />
                  <span class="ml-2 text-secondary">Image Processing: Running...</span>
                </div>
                <div v-else-if="progress.image === 'complete'">
                  <font-awesome-icon :icon="['far', 'check-circle']" :style="{ color: '#28a745' }" />
                  <span class="ml-2 text-secondary">Image Processing: Completed</span>
                </div>
              </b-list-group-item>
              <b-list-group-item class="d-flex align-items-center pt-1 pb-1 pl-2 pr-2">
                <template v-if="progress.hash === 'waiting'">
                  <font-awesome-icon :icon="['far', 'clock']" :style="{ color: '#6c757d' }" />
                  <span class="ml-2 text-secondary align-middle">Generate Image Hash: Waiting to start...</span>
                </template>
                <template v-if="progress.hash === 'running'">
                  <font-awesome-icon :icon="['fas', 'spinner']" :style="{ color: '#6c757d' }" spin />
                  <span class="ml-2 text-secondary align-middle">Generate Image Hash: Running...</span>
                </template>
                <template v-if="progress.hash === 'complete'">
                  <font-awesome-icon :icon="['far', 'check-circle']" :style="{ color: '#28a745' }" />
                  <span class="ml-2 text-secondary align-middle">Generate Image Hash: Completed</span>
                </template>
              </b-list-group-item>
              <b-list-group-item class="d-flex flex-wrap align-items-center pt-1 pb-1 pl-2 pr-2">
                <template v-if="progress.duplicates === 'waiting'">
                  <font-awesome-icon :icon="['far', 'clock']" :style="{ color: '#6c757d' }" />
                  <span class="ml-2 text-secondary align-middle">Duplicates Check: Waiting to start...</span>
                </template>
                <template v-else-if="progress.duplicates === 'running'">
                  <font-awesome-icon :icon="['fas', 'spinner']" :style="{ color: '#6c757d' }" spin />
                  <span class="ml-2 text-secondary align-middle">Duplicates Check: Running...</span>
                </template>
                <template v-else-if="progress.duplicates === 'complete'">
                  <font-awesome-icon :icon="['far', 'check-circle']" :style="{ color: '#28a745' }" />
                  <span class="ml-2 text-secondary align-middle"
                    >Duplicates Check: Completed -> No duplicates found.</span
                  >
                </template>
                <template v-else>
                  <font-awesome-icon :icon="['far', 'times-circle']" :style="{ color: '#dc354c' }" />
                  <span class="ml-2 text-secondary align-middle">Duplicates Check: Failure</span>
                  <b-alert variant="danger" class="w-100 mb-0 pt-2 pb-2 pl-3 pr-3" show>
                    The same image already exists. Try to upload a different one.
                  </b-alert>
                </template>
              </b-list-group-item>
            </b-list-group>
          </div>
          <b-form-group
            id="input-group-1"
            label="Name:"
            label-for="input-1"
            description="Enter a name which describes the POI best (e.g. name of the location, etc.)."
            class="mt-2 mb-0"
          >
            <b-form-input
              id="input-1"
              v-model="form.name"
              placeholder="Please enter POI name (or leave blank)..."
              trim
              :disabled="uploading"
            ></b-form-input>
          </b-form-group>
          <b-form-group
            id="input-group-2"
            label="Description:"
            label-for="input-2"
            description="Some more words to describe the POI. E.g. what is so special about this place, etc.."
            class="mt-2 mb-0"
          >
            <b-form-textarea
              id="input-2"
              v-model="form.description"
              placeholder="Please enter POI description (or leave blank)..."
              rows="3"
              max-rows="8"
              trim
              :disabled="uploading"
            ></b-form-textarea>
          </b-form-group>
        </b-col>
        <b-col>
          <b-form-group
            label="Categories:"
            description="Select the categories you would like to apply to the POI."
            class="mt-0 mb-0"
          >
            <treeselect v-model="categories" :options="optionsCategories" disable-branch-nodes multiple show-count>
              <div slot="value-label" slot-scope="{ node }">
                {{ node.raw.customLabel ? node.raw.customLabel : node.raw.label }}
              </div>
            </treeselect>
          </b-form-group>
          <b-form-group
            label="Hashtags:"
            description="Select the hashtags you would like to apply to the POI."
            class="mt-2 mb-0"
          >
            <treeselect v-model="hashtags" :options="optionsHashtags" disable-branch-nodes multiple show-count>
              <div slot="value-label" slot-scope="{ node }">
                {{ node.raw.customLabel ? node.raw.customLabel : node.raw.label }}
              </div>
            </treeselect>
          </b-form-group>
          <b-form-group label="Weather Conditions:" description="Select the weather conditions." class="mt-2 mb-0">
            <treeselect v-model="weather" :options="optionsWeather" disable-branch-nodes multiple show-count>
              <div slot="value-label" slot-scope="{ node }">
                {{ node.raw.customLabel ? node.raw.customLabel : node.raw.label }}
              </div>
            </treeselect>
          </b-form-group>

          <div class="mt-1">
            <b-button
              type="submit"
              variant="primary"
              :disabled="progress.status !== 'complete' || progress.failures.length > 0"
            >
              Submit
              <font-awesome-icon v-if="uploading" :icon="['fas', 'spinner']" spin />
            </b-button>
            <b-button
              type="reset"
              variant="warning"
              class="ml-1"
              :disabled="uploading || !['waiting', 'complete'].includes(progress.status)"
            >
              Reset Form
            </b-button>
            <b-button
              :to="{ name: 'pois-list' }"
              class="ml-1 float-right"
              :disabled="uploading || !['waiting', 'complete'].includes(progress.status)"
            >
              Cancel
            </b-button>
            <b-progress v-if="percentage" class="mt-2" max="100">
              <b-progress-bar :value="percentage" :label="`${percentage.toFixed()}%`"></b-progress-bar>
            </b-progress>
          </div>
        </b-col>
      </b-row>
    </b-form>
  </b-card>
</template>

<script>
import { mapGetters } from 'vuex';

import exifr from 'exifr';
// alternative to jimp: https://www.therogerlab.com/how-can-i/javascript/resize-an-image.html
import jimp from 'jimp';
import path from 'path';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';

import { db, storage } from '@/main';

export default {
  name: 'PoisForm',
  components: { Treeselect },
  data() {
    return {
      categories: [],
      hashtags: [],
      weather: [],
      check: {
        // hashes to compare images (source: https://www.npmjs.com/package/jimp#comparing-images)
        hash: null, // base 64 hash
        pHash: null, // base 2 hash
      },
      errors: [],
      exif: {},
      form: {
        description: '',
        images: { orig: null, preview: null },
        name: '',
      },
      percentage: 0,
      // progress status
      progress: {
        exif: 'waiting',
        image: 'waiting',
        hash: 'waiting',
        duplicates: 'waiting',
        failures: [],
        // the overall progress status (set to "complete" at the very end)
        status: 'waiting',
      },
      show: true,
      submitSuccess: false,
    };
  },
  computed: {
    ...mapGetters({
      config: 'config/records',
      user: 'auth/user',
    }),
    imagePreviewUrl() {
      return this.form.images.preview ? URL.createObjectURL(this.form.images.preview) : null;
    },
    optionsCategories() {
      return this.config?.optionsCategories ? this.config.optionsCategories : [];
    },
    optionsHashtags() {
      return this.config?.optionsHashtags ? this.config.optionsHashtags : [];
    },
    optionsWeather() {
      return this.config?.optionsWeather ? this.config.optionsWeather : [];
    },
    uploading() {
      return this.percentage > 0;
    },
  },
  methods: {
    checkForm: function(evt) {
      evt.preventDefault();
      this.errors = [];

      if (!this.exif.gps) {
        this.errors.push({
          field: 'Image',
          error: 'Required GPS coordinates not found.',
          fix: 'Please upload another image.',
        });
        return true;
      }

      if (this.exif.ratio < 3) {
        this.errors.push({
          field: 'Image',
          error: `Invalid width:height ratio of ${this.exif.ratio} (${this.exif.width}x${this.exif.height}px). Currently only panorama pictures with a width:height ratio greater than 3:1 (3.0) are accepted.`,
          fix: 'Please upload a panorama image with a width:height ratio greater 3.0.',
        });
        return true;
      }

      return this.errors.length;
    },
    onSubmit(evt) {
      if (this.checkForm(evt)) {
        return true;
      }
      const formData = new FormData();
      formData.append('name', this.form.name);
      formData.append('description', this.form.description);
      formData.append('images', this.form.images.orig);
      // ToDo: submit data
    },
    resetFormFields(evt) {
      if (evt) {
        evt.preventDefault();
      }
      // reset form data
      this.errors = [];
      this.form = { description: '', images: { orig: null, preview: null }, name: '' };
      this.progress = {
        exif: 'waiting',
        image: 'waiting',
        hash: 'waiting',
        duplicates: 'waiting',
        failures: [],
        status: 'waiting',
      };
      this.categories = [];
      this.hashtags = [];
      this.weather = [];
      // trick to reset/clear native browser form validation state
      this.show = false;
      this.submitSuccess = false;
      this.$nextTick(() => {
        this.show = true;
      });
    },
    async onFileChange(evt) {
      this.resetFormFields();
      if (evt.target.files.length) {
        this.progress.status = 'running';
        this.progress.exif = 'running';
        const file = evt.target.files[0];
        await this.processExifData(file);

        if (this.exif.gps) {
          this.progress.exif = 'complete';
        } else {
          this.progress.exif = 'complete-failure';
          this.progress.failures.push('No GPS Data');
          this.progress.status = 'complete';
          // stop processing
          return false;
        }

        this.progress.image = 'running';
        await jimp
          .read(URL.createObjectURL(file))
          .then(async image => {
            const image0600 = image.clone();
            image0600
              .resize(600, jimp.AUTO)
              .quality(75) // set quality
              .getBufferAsync(jimp.AUTO)
              .then(buffer => {
                this.form.images.image0600 = new Blob([buffer], { type: 'image/png' });
              })
              .catch(e => {
                this.errors.push(e);
              });

            const image1200 = image.clone();
            await image1200
              .resize(1200, jimp.AUTO)
              .quality(75)
              .getBufferAsync(jimp.AUTO)
              .then(async buffer => {
                const blob = new Blob([buffer], { type: 'image/png' });
                this.form.images.preview = blob;
                this.form.images.image1200 = blob;

                await jimp.read(buffer).then(image => {
                  this.progress.hash = 'running';
                  this.check.hash = image.hash();
                });
                await jimp.read(buffer).then(image => {
                  this.check.pHash = image.hash(2);
                });
                //Promise.all([hash, pHash]).then(() => (this.progress.hash = 'complete'));
                this.progress.hash = 'complete';
              })
              .catch(e => {
                this.errors.push(e);
              });

            const image1900 = image.clone();
            image1900
              .resize(1900, jimp.AUTO)
              .quality(75)
              .getBufferAsync(jimp.AUTO)
              .then(buffer => {
                this.form.images.image1900 = new Blob([buffer], { type: 'image/png' });
              })
              .catch(e => {
                this.errors.push(e);
              });

            Promise.all([image0600, image1200, image1900]).finally(() => {
              this.progress.image = 'complete';
            });
          })
          .catch(err => {
            this.errors.push(err);
          });

        this.progress.duplicates = 'running';
        const duplicates = await db
          .collection('pois')
          .where('check.hash', '==', this.check.hash)
          .get()
          .then(snap => {
            const docIds = [];
            snap.forEach(doc => {
              docIds.push(doc.id);
              this.progress.failures.push({ duplicate: doc.id });
            });
            return docIds;
          })
          .catch(error => {
            console.error('Error querying documents:', error);
          });
        this.progress.duplicates = duplicates.length ? 'complete-duplicates' : 'complete';
        // overall progress status is COMPLETE -> yipeee :)
        this.progress.status = 'complete';
      }
    },
    async processExifData(file) {
      let exifr_error;
      await exifr
        .parse(file)
        .then(() => {})
        .catch(e => {
          exifr_error = e;
        });
      if (!exifr_error) {
        // execute the following only if no errors
        this.exif.gps = await exifr.gps(file);
        this.exif.file_ext = path.extname(file.name.toLowerCase());
        if (this.exif.gps) {
          // continue only if there are exif gps coordinates
          let output = await exifr.parse(file, ['DateTimeOriginal', 'ExifImageWidth', 'ExifImageHeight']);
          this.exif.width = output.ExifImageWidth;
          this.exif.height = output.ExifImageHeight;
          this.exif.taken_at = output.DateTimeOriginal;
          this.exif.ratio = parseFloat((this.exif.width / this.exif.height).toFixed(2));
        }
      }
    },
    onUpload(evt) {
      if (this.checkForm(evt)) {
        return true;
      }
      // create document reference (but do not save it yet!)
      // https://firebase.google.com/docs/firestore/manage-data/add-data#add_a_document
      const newPoisRef = db.collection('pois').doc();

      const bucket = `pois/${newPoisRef.id}/`;

      const taskUpload0600 = storage.ref(`${bucket}0600`).put(this.form.images.image0600);
      const taskUpload1200 = storage.ref(`${bucket}1200`).put(this.form.images.image1200);
      //const taskUpload1900 = storage.ref(`${bucket}1900`).put(this.form.images.image1900);

      //const storageRef = storage.ref(`${bucket}orig`);
      //const taskUpload = storageRef.put(this.form.images.orig);
      const storageRef = storage.ref(`${bucket}1900`);
      const taskUpload1900 = storageRef.put(this.form.images.image1900);
      taskUpload1900.on(
        'state_changed',
        snapshot => {
          this.percentage = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
        },
        e => {
          this.errors.push(e.message);
        },
        () => {
          this.percentage = 0; // set to 0 on complete to hide progress bar for next upload
        }
      );

      //Promise.all([taskUpload, taskUpload0600, taskUpload1200, taskUpload1900])
      Promise.all([taskUpload0600, taskUpload1200, taskUpload1900])
        .then(() => {
          const poiData = {
            // store poi ref id to simplify lookup
            id: newPoisRef.id,
            name: this.form.name,
            description: this.form.description,
            geojson: {
              type: 'Point',
              properties: {
                categories: this.categories,
                hashtags: this.hashtags,
                weather: this.weather,
                taken_at: this.exif.taken_at,
              },
              coordinates: [this.exif.gps.longitude, this.exif.gps.latitude],
            },
            bucket: bucket,
            check: this.check,
            // flag to be set to true when image was validated (so no odd stuff is uploaded)
            validated: false,
            created_by: this.user.data.uid,
            created_at: new Date(),
            updated_by: null,
            updated_at: null,
          };

          // store db record after file has been uploaded successfully
          newPoisRef.set(poiData).catch(e => {
            this.errors.push(e.message);
          });
        })
        .catch(e => {
          this.errors.push(e.message);
          // delete image from storage in case of errors
          storageRef
            .delete()
            .then(() => {
              // ToDo: display success -> toast?
            })
            .catch(e => {
              this.errors.push(e.message);
            });
        })
        .finally(() => {
          this.submitSuccess = true;
        });
    },
  },
  mounted() {
    this.$store.dispatch('config/bindRecords');
  },
  beforeRouteLeave(to, from, next) {
    this.resetFormFields();
    next();
  },
};
</script>

<style scoped></style>
